/**
 * `prefetchDocument(url)` asks the browser to fetch an app's document ahead of a tap:
 * one `<link rel="prefetch" as="document">` per URL per page, nothing when the member
 * asked to save data. Chromium keeps the response for a few minutes; Safari ignores
 * `rel="prefetch"`, so on iOS this is a no-op (docs/architecture.md §6).
 */
type LinkLike = { rel: string; as: string; href: string };
type Env = {
  head: { appendChild(node: LinkLike): unknown };
  createLink(): LinkLike;
  baseUrl: string;
  saveData: boolean;
};

export function createPrefetcher(env: () => Env | null): (url: string) => void {
  const seen = new Set<string>();
  return (url) => {
    const e = env();
    if (!e || e.saveData) return;
    let href: string;
    try {
      href = new URL(url, e.baseUrl).href;
    } catch {
      return;
    }
    if (seen.has(href)) return;
    seen.add(href);
    const link = e.createLink();
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = href;
    e.head.appendChild(link);
  };
}

export const prefetchDocument = createPrefetcher(() => {
  if (typeof document === 'undefined') return null;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return {
    head: document.head as unknown as Env['head'],
    createLink: () => document.createElement('link'),
    baseUrl: document.baseURI,
    saveData: connection?.saveData === true,
  };
});
